import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { contactsAPI, contactsEndpoints } from '@/api/contacts';
import { conversationsEndpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { contactSearchTerm } from '@/modules/contacts/constants';
import { matchesContactIdentity } from '@/modules/contacts/utils/identity';
import { API } from '@/modules/humanChat/hooks/useConversations';
import type { Conversation } from '@/shared/types/chat';
import type { Contact, ContactsResponse } from '@/shared/types/contacts';

import { useSessionContent } from './useSessionContent';
import {
  selectContactId,
  selectReplayPhase,
  selectSessionStart,
  selectTryItSessionId,
} from '../redux/selectors';

export type SessionContact = { contact?: Contact; isResolving: boolean };

/**
 * Reads a contact by id, sharing `useContact`'s cache entry but not its unused
 * mutations. `isDead` covers the 404 a stale id gives.
 */
const useContactById = (contactId?: string) => {
  const { data, status } = useQuery<Contact>({
    queryKey: [contactsEndpoints.contact(contactId ?? '')],
    queryFn: () => contactsAPI.getContact(contactId!),
    enabled: !!contactId,
    // The whole fallback chain waits on this, so a retried network error would
    // freeze the panel for 3 attempts.
    retry: false,
    staleTime: Infinity,
  });
  return {
    contact: data,
    isPending: !!contactId && status === 'pending',
    isDead: status === 'error',
  };
};

/**
 * Finds the contact whose own email / phone / external id equals `term`. `q` is a
 * fuzzy search, so only an exact identity match counts — linking to a same-name
 * contact would be worse than no link at all.
 */
const useContactByIdentity = (term?: string) => {
  const identityTerm = term?.trim();
  const searchTerm = contactSearchTerm(identityTerm);
  const { data, status } = useQuery<ContactsResponse>({
    queryKey: ['contacts/search', searchTerm],
    queryFn: () =>
      callGet(contactsEndpoints.contacts(new URLSearchParams({ limit: '20', q: searchTerm! }))),
    enabled: !!searchTerm,
    staleTime: Infinity,
  });
  const contact = identityTerm
    ? data?.contacts?.find((candidate) => matchesContactIdentity(candidate, identityTerm))
    : undefined;
  return { contact, isPending: !!searchTerm && status === 'pending' };
};

/**
 * Contact linked to the replayed session, or `undefined` when the end user is
 * not a contact.
 *
 * The analytics payload has no `contact_id` column, so it is resolved in three
 * steps, each running only once the previous one is known to have failed:
 *  1. `context.user.contact_id` off the timeline — free, already in the store.
 *  2. The desk conversation, the authoritative source.
 *  3. A contact search on the session's channel user id, then its email.
 *
 * Ids from steps 1-2 are only used once the contact API confirms they resolve:
 * a stale id would otherwise link to a "Contact not found" page. `isResolving`
 * lets the panel tell "not a contact" from "still looking".
 */
export const useSessionContactId = (): SessionContact => {
  const contactIdFromTurns = useSelector(selectContactId);
  const sessionId = useSelector(selectTryItSessionId);
  const sessionStart = useSelector(selectSessionStart);
  const replayPhase = useSelector(selectReplayPhase);
  const { data: content } = useSessionContent(sessionId, sessionStart);
  const deskId = content?.desk_id;

  // The payload resolves one render before `replayContentLoaded` commits the turns;
  // without this the timeline always looks empty and step 1 never gets a chance.
  const timelineSettled = replayPhase !== 'loading';

  const fromTurns = useContactById(contactIdFromTurns);

  // Also covers a timeline id that turned out to be dead.
  const conversationEnabled =
    timelineSettled && !!deskId && (!contactIdFromTurns || fromTurns.isDead);
  const { data: conversation, status: conversationStatus } = useQuery<Conversation, Error>({
    queryKey: [conversationsEndpoints.conversation(deskId ?? '', sessionId ?? '')],
    queryFn: () => API.getConversation(deskId!, sessionId!),
    enabled: conversationEnabled,
    // A replayed session may be long gone from the desk; re-asking only 404s again.
    retry: false,
    staleTime: Infinity,
  });
  const conversationPending = conversationEnabled && conversationStatus === 'pending';

  const fromConversation = useContactById(conversation?.context?.user?.contact_id);

  const resolvedById = fromTurns.contact ?? fromConversation.contact;
  const idStepsPending = fromTurns.isPending || conversationPending || fromConversation.isPending;

  // Channel user id first, then email — a session almost always carries the former.
  const searchEnabled = timelineSettled && !resolvedById && !idStepsPending;
  const byChannelUser = useContactByIdentity(
    searchEnabled ? (content?.channel_user_id ?? undefined) : undefined
  );
  const emailSearchEnabled = searchEnabled && !byChannelUser.contact && !byChannelUser.isPending;
  const byEmail = useContactByIdentity(
    emailSearchEnabled ? (content?.user_email ?? undefined) : undefined
  );

  const contact = resolvedById ?? byChannelUser.contact ?? byEmail.contact;
  // `!timelineSettled` counts as resolving: every step is gated on it, and the panel
  // must not read "not a contact" before the turns commit.
  const isResolving =
    !contact &&
    (!timelineSettled || idStepsPending || byChannelUser.isPending || byEmail.isPending);

  return { contact, isResolving };
};
